/**
 * theapp.global — Webflow custom code (served from jsDelivr as dist/theapp.js + dist/theapp.css)
 * dist/theapp.css = styles/*.css: the CSS the script's output depends on (marquee animation, activations row, register tabs,
 * Swiper core layout for the carousel bundle).
 * ------------------------------------------------------------------
 * Every feature is an independent, idempotent pass over data-* hooks and runs in isolation:
 * a throw in one can't take the others down. Only navScroll depends on webflow.js (IX3).
 *
 *   renderLists        [data-stream-list] / [data-stream-broadcast]      rich text → cloned [data-card-template] cards
 *   renderPricing      [data-section-pricing] + [data-pricing-{type}]     rich text → title / price / link targets
 *   runCountdowns      [data-countdown] + [data-days|hours|minutes|seconds]
 *   addToCalendar      [data-gcal] [data-outlook] [data-apple] → deep links / .ics
 *   mapLinks           [data-map-link] + [data-map-line1|city|state|zip] → Google Maps search URL
 *   externalLinks      [data-external] a, a[data-external] → new tab
 *   renderActivations  [data-ewhat-content] rich text → .e_what_card articles (+ dragScroll on desktop)
 *   registerTabs       [data-register-tabs="wrap"] + [data-register-tab|pane] → mobile tab switching
 *   initMarquees       [data-marquee] → infinite scroller; window.WebflowMarquee { init, destroy }
 *   liteYoutube        lite-youtube[videoid] → full YouTube URL rewritten to the bare video ID
 *   loadCarousel       [data-slider-instance] → injects dist/theapp-carousel.js (Swiper; see src/carousel.ts)
 *   navScroll          scroll direction → IX3 events "nav-scroll-up" / "nav-scroll-down"
 *   fillText           [event_full] [event_start] tokens → CMS values (runs last)
 *
 * Shared inputs, read from whichever elements carry them (one hidden "event data" div is ideal):
 * [data-event-start] [data-event-end] [data-event-full] [data-date-timezone], optional data-tz.
 */

import './styles/activations.css';
import './styles/carousel.css';
import './styles/marquee.css';
import './styles/register-tabs.css';
import { dragScroll, renderActivations } from './features/activations';
import { addToCalendar } from './features/calendar';
import { loadCarousel } from './features/carousel';
import { runCountdowns } from './features/countdowns';
import { externalLinks } from './features/external-links';
import { fillText } from './features/fill-text';
import { liteYoutube } from './features/lite-youtube';
import { mapLinks } from './features/map-links';
import { destroyMarquees, initMarquees } from './features/marquee';
import { navScroll } from './features/nav-scroll';
import { renderPricing } from './features/pricing';
import { registerTabs } from './features/register-tabs';
import { renderLists } from './features/render-lists';
import { EMPTY_CONTEXT, type EventContext, eventContext } from './lib/event-context';
import { error } from './lib/log';
import { onWebflowReady } from './lib/webflow';

type Feature = (ctx: EventContext) => void;

/* ---------- boot ---------- */

// Object, not array: the keys are string literals, so feature names survive minification in error logs.
// Insertion order is preserved for non-integer keys — fillText stays last.
export const FEATURES: Record<string, Feature> = {
  renderLists,
  renderPricing,
  runCountdowns,
  addToCalendar,
  mapLinks,
  externalLinks,
  renderActivations,
  dragScroll,
  registerTabs,
  initMarquees,
  liteYoutube,
  loadCarousel,
  fillText, // last: tokens inside anything rendered above get filled too
};

// Each step runs in isolation: a throw in one can't take the others down.
const run = <A extends unknown[], R>(name: string, fn: (...args: A) => R, ...args: A): R | undefined => {
  try {
    return fn(...args);
  } catch (err) {
    error(`${name} failed:`, err);
    return undefined;
  }
};
const init = () => {
  const ctx = run('eventContext', eventContext) ?? EMPTY_CONTEXT;
  Object.entries(FEATURES).forEach(([name, fn]) => run(name, fn, ctx));
};

window.WebflowMarquee = { init: initMarquees, destroy: destroyMarquees };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
onWebflowReady(() => run('navScroll', navScroll)); // needs webflow.js + IX3, so it boots through Webflow's queue, not ours
